/* eslint-disable camelcase */
import {
  GetUserTimeZone,
  TimeZoneAbbreviated,
  removeOfficeImageFromAddress,
} from 'src/latest/src';
import makeApiRequest from '../../api/makeApiCall';
import { Config } from '../../latest/src/constants/AppServerConfig';
import moment from 'moment';

const CALENDER_SERVICE = Config.calenderService;

export const getTherapistSchedules = async (data, offset = 0, limit = 10) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/_schedules?offset=${offset}&limit=${limit}`,
    'POST',
    data,
  );
};

export const getScheduleSlotStatus = async (slotId: String) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/schedules/${slotId}/_status`,
  );
};

// API to block the slot

export const blockScheduleSlot = async (data: Object, slotId: String) => {
  if (data) {
    const currentDate = new Date();

    data.user_time_zone = GetUserTimeZone();

    data.user_time_zone_key = TimeZoneAbbreviated(currentDate.toDateString());

    if (data?.office_address) {
      data.office_address = removeOfficeImageFromAddress(data.office_address);
    }
  }

  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/schedules/${slotId}/_block`,
    'POST',
    data,
  );
};

export const getSchedules = async (
  data: Object,
  offset: Number = 0,
  limit: Number = 100,
) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/_schedules?limit=${limit}&offset=${offset}`,
    'POST',
    data,
  );
};

export const createEvent = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/events`,
    'POST',
    data,
  );
};

export const deleteAllTypeEvent = async (
  eventId: String,
  isRecurrent: Boolean,
  selectedOption: String | null,
) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/events/${eventId}${
      selectedOption === `Deletethewholeseries`
        ? `?delete_series=${isRecurrent}`
        : selectedOption === `Deletefutureevent`
        ? `?delete_future_events=${isRecurrent}`
        : ''
    } `,
    'DELETE',
  );
};

export const editEvent = async (eventId: String, data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}event-rule/${eventId}`,
    'PUT',
    data,
  );
};

export const getSchedulesV2 = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}event-rule/_search`,
    'POST',
    data,
  );
};

export const getAppointments = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/appointment/_search`,
    'POST',
    data,
  );
};

export const getClientAppointments = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/appointment/client/_search`,
    'POST',
    data,
  );
};

export const deleteAppointment = async (eventId: String) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/appointment/${eventId} `,
    'DELETE',
  );
};

export const editAppointment = async (eventId: String, data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/appointment/${eventId} `,
    'PUT',
    data,
  );
};

export const deleteEventRule = async (
  eventId: String,
  deleteFutureEvents: boolean,
  deleteSeries: boolean,
  eventDate: string | undefined,
  new_r_rule: string | null = '',
  updated_r_rule: string | null = '',
) => {
  const eventDateTime = moment(eventDate).format('YYYY-MM-DD');

  return await makeApiRequest(
    `${CALENDER_SERVICE}event-rule/${eventId}?delete_future_events=${deleteFutureEvents}&delete_series=${deleteSeries}&event_date=${eventDateTime}&new_r_rule=${new_r_rule}&updated_r_rule=${updated_r_rule}`,
    'DELETE',
  );
};

export const getGoogleStatus = async therapistId => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}g-calendar/oauth/_status?therapist_id=${therapistId}`,
    'GET',
  );
};

export const initiateGoogleAuth = async therapistId => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}g-calendar/oauth/_initiate`,
    'POST',
    {
      therapist_id: therapistId,
      ui_redirect_path: 'therapy/account',
    },
  );
};

export const completeOAuthFlow = async manualUrlString => {
  let searchParams;

  if (manualUrlString) {
    const queryString = manualUrlString.includes('?')
      ? manualUrlString.substring(manualUrlString.indexOf('?'))
      : `?${manualUrlString}`;

    searchParams = new URLSearchParams(queryString);
  } else {
    searchParams = new URLSearchParams(window.location.search);
  }

  const code = searchParams.get('code');
  const state = searchParams.get('state');

  if (!code) {
    throw new Error('No authorization code found');
  }

  let callbackUrl = `${CALENDER_SERVICE}g-calendar/oauth/_callback?code=${encodeURIComponent(
    code,
  )}`;

  if (state) {
    callbackUrl += `&state=${encodeURIComponent(state)}`;
  }

  callbackUrl += `&ui_redirect_path=${encodeURIComponent('therapy/account')}`;

  return await makeApiRequest(callbackUrl, 'GET');
};

export const triggerManualSync = async (therapistId, syncWindowDays = 30) => {
  const payload = {
    therapist_id: therapistId,
    sync_window_days: syncWindowDays,
  };

  return await makeApiRequest(
    `${CALENDER_SERVICE}g-calendar/_sync`,
    'POST',
    payload,
  );
};

export const disconnectGoogleCalendar = async (
  therapistId: string,
  resetData: boolean,
) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}g-calendar/oauth/_disconnect`,
    'DELETE',
    { therapist_id: therapistId, reset_data: resetData },
  );
};

export const saveAvailability = async (therapistId, scheduleData) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}availability/settings`,
    'POST',
    {
      therapist_id: therapistId,
      schedule: scheduleData,
    },
  );
};

export const getProviderWorkingHours = async (providerId: string) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}providers/${providerId}/working-hours`,
    'GET',
  );
};

export const updateProviderWorkingHours = async (
  providerId: string,
  workingHoursData: Object,
) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}providers/${providerId}/working-hours`,
    'PUT',
    workingHoursData,
  );
};

export const fetchEventsAnalysis = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/_event-analysis`,
    'POST',
    data,
  );
};

export const searchEvents = async (data: Object) => {
  return await makeApiRequest(
    `${CALENDER_SERVICE}calendar/events/_search`,
    'POST',
    data,
  );
};
